import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatKRW } from '@/lib/format';

interface SettingsRow {
  monthly_price_krw: number;
  grace_days: number;
  retry_schedule: number[];
  updated_at: string | null;
}

export function AdminPricingPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-subscription-settings'],
    queryFn: async (): Promise<SettingsRow | null> => {
      const { data, error } = await supabase
        .from('subscription_settings')
        .select('monthly_price_krw, grace_days, retry_schedule, updated_at')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [price, setPrice] = useState('');
  const [graceDays, setGraceDays] = useState('');
  const [retry, setRetry] = useState('');

  useEffect(() => {
    if (data) {
      setPrice(String(data.monthly_price_krw));
      setGraceDays(String(data.grace_days));
      setRetry(data.retry_schedule.join(','));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        monthly_price_krw: parseInt(price, 10),
        grace_days: parseInt(graceDays, 10),
        retry_schedule: retry
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => Number.isFinite(n)),
      };
      const { error } = await supabase.from('subscription_settings').update(payload).eq('id', 1);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-subscription-settings'] }),
  });

  if (isLoading) return <div className="text-ink-muted">불러오는 중…</div>;

  return (
    <div className="card space-y-5">
      <h1 className="text-2xl font-bold">구독 가격 설정</h1>
      <div>
        <label className="label" htmlFor="price">월 구독료 (원)</label>
        <input
          id="price"
          className="field"
          inputMode="numeric"
          value={price}
          onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))}
        />
        <p className="text-base text-ink-muted mt-1">표시: {formatKRW(Number(price) || 0)}</p>
      </div>
      <div>
        <label className="label" htmlFor="grace">결제 실패 유예 일수</label>
        <input
          id="grace"
          className="field"
          inputMode="numeric"
          value={graceDays}
          onChange={(e) => setGraceDays(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <div>
        <label className="label" htmlFor="retry">재시도 일정 (쉼표 구분, 일차)</label>
        <input
          id="retry"
          className="field"
          value={retry}
          onChange={(e) => setRetry(e.target.value)}
          placeholder="1,3,5"
        />
      </div>
      {save.error && <p className="text-danger">{(save.error as Error).message}</p>}
      <button
        className="btn-primary w-full"
        onClick={() => save.mutate()}
        disabled={save.isPending}
      >
        {save.isPending ? '저장 중…' : '저장'}
      </button>
    </div>
  );
}
